import { mapContactsToCSVFormat } from 'pages/contacts/export/contactExportMapper';
import { mapCorrespondencesToCSVFormat } from 'pages/correspondence/export/correspondenceExportMapper';
import { mapDebriefingsToCSVFormat } from 'pages/debriefing/export/debriefingExportMapper';
import { mapEffortsToCSVFormat } from 'pages/efforts/export/effortExportMapper';
import { mapExpensesToCSVFormat } from 'pages/expenses/export/expenseExportMapper';
import { mapMatterExperienceRecordsToCSVFormat } from 'pages/experience/export/matterExperienceRecordExportMapper';
import { mapInvoicesToCRFormat } from 'pages/invoices/export/invoiceCRExportMapper';
import { mapInvoicesToCSVFormat } from 'pages/invoices/export/invoiceExportMapper';
import { mapMarketingProfilesToCSVFormat } from 'pages/marketing-profiles/export/marketingProfileExportMapper';
import { mapMattersToCSVFormat } from 'pages/matters/export/matterExportMapper';
import { mapPrepaymentsToCSVFormat } from 'pages/prepayments/export/prepaymentExportMapper';
import { mapRankingsRefereesToCSVFormat } from 'pages/rankings-referees/export/rankingsRefereeExportMapper';
import { mapTimecardsToCompactFormat } from 'pages/timecards/export/timecardCompactExportMapper';
import { mapTimecardsToCSVFormat } from 'pages/timecards/export/timecardExportMapper';
import { t } from 'utils/getTranslations';
import { getLocalStorage, removeLocalStorage } from 'utils/localStorage';
import { ClassifierData } from './classifiers';

export type ExportStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export type ExportEntityType =
    | 'TIMECARD'
    | 'TIMECARD_COMPACT'
    | 'INVOICE'
    | 'INVOICE_CR'
    | 'PREPAYMENT'
    | 'EXPENSE'
    | 'MATTER'
    | 'PARTY'
    | 'MARKETING_PROFILE'
    | 'CORRESPONDENCE'
    | 'DEBRIEFING'
    | 'EFFORT'
    | 'RANKINGS_REFEREE'
    | 'MATTER_EXPERIENCE_RECORD';

// Type for mapper functions
export type ExportMapperOptions = {
    isTwoDecimalEnabled?: boolean;
    // TIMECARD_BILLED_TIME_CONTROL — gates the timecard export's billing-uplift columns.
    isBilledTimeControlEnabled?: boolean;
    // The ADMINISTRATOR_FIELD setting — splits case assistants out of the Handled by column,
    // matching what the case view shows.
    isAdministratorFieldEnabled?: boolean;
    // LAST_HCS_MEETING — gates the contacts export's "Last {type}" column.
    isLastHcsMeetingEnabled?: boolean;
    // RANKINGS_REFEREE — names the experience records export's referee column "Referee candidates".
    isRankingsRefereeEnabled?: boolean;
};

// Rows for an `.xlsx` export may hold `ExcelNumber` and `Date` cells (`components/xlsx/excelCells`),
// which only `generateStyledXLSX` writes; rows for `generateCSV` must hold plain strings and numbers.
type ExportMapper<T = unknown> = (
    data: T[],
    classifiers: Record<string, ClassifierData>,
    options?: ExportMapperOptions
) => Record<string, unknown>[];

// Configuration for each export entity type. `mapper`'s element type is intentionally `any`
// because `EXPORT_CONFIG` is a heterogeneous lookup: each entry's mapper takes a different DTO
// (`InvoiceExportListItemDto`, `ContactListExportItem`, …). With function parameters being
// contravariant, no single non-`any` element type accepts mappers across all DTOs — `unknown`
// rejects them and `never` accepts but corrupts callers. Per-entry mappers stay individually
// typed at their declaration sites in `pages/*/export/*.ts`. The slot here is the bivariance
// escape hatch for the heterogeneous map only.
export interface ExportEntityConfig {
    fileName: string;
    taskType: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous mapper map; see note above
    mapper: ExportMapper<any>;
    classifierCodes: string[];
    csvHeaders?: string[];
    // Options for `generateStyledXLSX`. `sumColumns` are mapper output header keys that get a bold
    // SUM row; they are resolved lazily so translations are loaded first.
    styledXLSX?: () => { sumColumns: string[] };
}

// Centralized config for export types - single source of truth
export const EXPORT_CONFIG: Record<ExportEntityType, ExportEntityConfig> = {
    TIMECARD: {
        fileName: 'timecards.xlsx',
        taskType: 'TIMECARD_EXPORT',
        mapper: mapTimecardsToCSVFormat,
        classifierCodes: [
            'TIMECARD_STATUS_TYPE',
            'LANGUAGE',
            // mapTimecardsToCSVFormat has always read these two; without them the Case status and
            // Case fee arrangement columns degraded to raw codes.
            'MATTER_STATE_TYPE',
            'PRICING_LIMITATION_TYPE',
            'TIMECARD_VALUE_FACTORS',
        ],
    },
    // Compact ("Menetluskulude raport") timecards export. Reuses the same backend export task
    // (`TIMECARD_EXPORT`) and raw data as TIMECARD; only the client-side mapper and the SUM row differ.
    TIMECARD_COMPACT: {
        fileName: 'timecards-compact.xlsx',
        taskType: 'TIMECARD_EXPORT',
        mapper: mapTimecardsToCompactFormat,
        classifierCodes: ['TIMECARD_STATUS_TYPE'],
        styledXLSX: () => ({
            sumColumns: [
                t('timecards.export.billed-time'),
                t('timecards.export.invoiced-billed-time'),
                t('timecards.export.timecard-amount-in-price-list-currency'),
                t('timecards.export.timecard-invoiced-amount-in-invoice-currency'),
            ],
        }),
    },
    INVOICE: {
        fileName: 'invoices.xlsx',
        taskType: 'INVOICE_EXPORT',
        mapper: mapInvoicesToCSVFormat,
        classifierCodes: [
            'INVOICE_TYPE',
            'INVOICE_STATUS_TYPE',
            'COUNTRY',
            'ACCOUNTING_REVENUE_TYPE',
            'PRICING_LIMITATION_TYPE',
        ],
    },
    INVOICE_CR: {
        fileName: 'invoices-cr.xlsx',
        taskType: 'INVOICE_CR_EXPORT',
        mapper: mapInvoicesToCRFormat,
        classifierCodes: [],
    },
    PREPAYMENT: {
        fileName: 'prepayments.xlsx',
        taskType: 'PREPAYMENT_EXPORT',
        mapper: mapPrepaymentsToCSVFormat,
        classifierCodes: ['INVOICE_STATUS_TYPE'],
    },
    PARTY: {
        fileName: 'contacts.xlsx',
        taskType: 'PARTY_EXPORT',
        mapper: mapContactsToCSVFormat,
        classifierCodes: [
            'LANGUAGE',
            'BUSINESS_AREA_TYPE',
            'SEGMENT_TYPE',
            'SERVICE_LEVEL_TYPE',
            'EFFORT_ACTIVITY_TYPE',
        ],
    },
    MARKETING_PROFILE: {
        fileName: 'marketing-profile.csv',
        taskType: 'MARKETING_PROFILE_EXPORT',
        mapper: mapMarketingProfilesToCSVFormat,
        classifierCodes: [],
        csvHeaders: ['Email', 'Name', 'Business Phone', 'Mobile Phone', 'Company list'],
    },
    MATTER: {
        fileName: 'cases.xlsx',
        taskType: 'MATTER_EXPORT',
        mapper: mapMattersToCSVFormat,
        classifierCodes: [
            'MATTER_STATE_TYPE',
            'INVOICE_STATUS_TYPE',
            'BUSINESS_AREA_TYPE',
            'REFERRAL_TYPE',
            'REFERRAL_OTHER_SOURCE_TYPE',
        ],
    },
    EXPENSE: {
        fileName: 'expenses.xlsx',
        taskType: 'EXPENSE_EXPORT',
        mapper: mapExpensesToCSVFormat,
        classifierCodes: ['EXPENSE_TYPE'],
    },
    CORRESPONDENCE: {
        fileName: 'correspondence.xlsx',
        taskType: 'CORRESPONDENCE_EXPORT',
        mapper: mapCorrespondencesToCSVFormat,
        classifierCodes: [
            'CORRESPONDENCE_TYPE',
            'CORRESPONDENCE_DIRECTION',
            'CORRESPONDENCE_STATUS_TYPE',
            'CORRESPONDENCE_CATEGORY',
        ],
    },
    EFFORT: {
        fileName: 'bd-efforts.xlsx',
        taskType: 'EFFORT_EXPORT',
        mapper: mapEffortsToCSVFormat,
        classifierCodes: ['EFFORT_ACTIVITY_TYPE'],
    },
    RANKINGS_REFEREE: {
        fileName: 'rankings-referees.xlsx',
        taskType: 'RANKINGS_REFEREE_EXPORT',
        mapper: mapRankingsRefereesToCSVFormat,
        classifierCodes: ['LEGAL_DIRECTORY'],
    },
    MATTER_EXPERIENCE_RECORD: {
        fileName: 'experience-records.xlsx',
        taskType: 'MATTER_EXPERIENCE_RECORD_EXPORT',
        mapper: mapMatterExperienceRecordsToCSVFormat,
        classifierCodes: ['MATTER_STATE_TYPE', 'BUSINESS_AREA_TYPE', 'COUNTRY'],
    },
    DEBRIEFING: {
        fileName: 'debriefings.xlsx',
        taskType: 'DEBRIEFING_EXPORT',
        mapper: mapDebriefingsToCSVFormat,
        classifierCodes: [],
    },
};

// Entity types that share another type's `taskType` (same backend task, different client-side
// mapper/writer). They are recovered after a refresh via the persisted `export:variant:` flag,
// so they must not own the reverse `taskType -> entityType` lookup.
const ALIAS_ENTITY_TYPES: ReadonlySet<ExportEntityType> = new Set<ExportEntityType>(['TIMECARD_COMPACT']);

// Reverse lookup: taskType -> entityType (canonical, non-alias entity per task type).
const TASK_TYPE_TO_ENTITY: Record<string, ExportEntityType> = Object.entries(EXPORT_CONFIG).reduce(
    (acc, [entityType, config]) => {
        if (!ALIAS_ENTITY_TYPES.has(entityType as ExportEntityType)) {
            acc[config.taskType] = entityType as ExportEntityType;
        }
        return acc;
    },
    {} as Record<string, ExportEntityType>
);

export const exportVariantStorageKey = (taskId: string) => `export:variant:${taskId}`;
/** The file type an export downloads as, read off the name it was started with. */
export const exportFormatOf = (fileName: string) => (fileName.endsWith('.csv') ? 'csv' : 'xlsx');
export const exportSurfaceStorageKey = (taskId: string) => `export:surface:${taskId}`;

/**
 * True for the first tab to see the task end while its surface is still stored, which it drops: the end is reported
 * once, however many tabs watch the task, and the key goes with it.
 */
export const claimExportSurface = (taskId: string) => {
    const key = exportSurfaceStorageKey(taskId);
    const isUnclaimed = getLocalStorage(key) != null;
    removeLocalStorage(key);
    return isUnclaimed;
};

// Response from GET /api/queue-tasks (list endpoint)
export interface ExportTaskListResponse {
    taskId: string;
    taskType: string;
    status: ExportStatus;
    createdAt: number;
    startedAt?: number;
    completedAt?: number;
    errorMessage?: string;
}

// Response from GET /api/queue-tasks/{taskId} (detail endpoint)
export interface ExportTaskResponse {
    taskId: string;
    taskType: string;
    status: ExportStatus;
    createdAt: number;
    startedAt?: number;
    completedAt?: number;
    result?: string; // JSON string containing export data
    errorMessage?: string;
    retryCount?: number;
    maxRetries?: number;
    executingInstance?: string;
}

interface ExportMetadata {
    entityType: string;
    totalRecords: number;
    exportedAt: number;
    filter?: string;
    sort?: string;
}

export interface ExportResultData<T = unknown> {
    data: T[];
    metadata: ExportMetadata;
}

export interface ActiveExport {
    taskId: string;
    entityType: ExportEntityType;
    status: ExportStatus;
    fileName: string;
    startedAt: number;
    completedAt?: number;
    errorMessage?: string;
    totalRecords?: number;
    /** The list the export was started from, as its usage events call it; none for an export no list started. */
    surface?: string;
}

// Map server response to ActiveExport for restoring tasks after page refresh
export const mapTaskListResponseToActiveExport = (task: ExportTaskListResponse): ActiveExport | null => {
    const canonicalEntityType = TASK_TYPE_TO_ENTITY[task.taskType];
    if (!canonicalEntityType) return null;

    // Recover an alias variant (e.g. the compact timecards export) persisted at trigger time.
    const persistedVariant = getLocalStorage(exportVariantStorageKey(task.taskId)) as ExportEntityType | null;
    const entityType = persistedVariant && persistedVariant in EXPORT_CONFIG ? persistedVariant : canonicalEntityType;

    const config = EXPORT_CONFIG[entityType];
    const fileName = getLocalStorage(`export:filename:${task.taskId}`) ?? config.fileName;
    return {
        taskId: task.taskId,
        entityType,
        status: task.status,
        fileName,
        startedAt: task.createdAt,
        completedAt: task.completedAt,
        errorMessage: task.errorMessage,
        surface: getLocalStorage(exportSurfaceStorageKey(task.taskId)) ?? undefined,
    };
};
